import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  addDefaultRate,
  fetchDefaultRateDetails,
  updateDefaultRate,
} from '../../common/api/defaultRatesService';
import { LOADING_STATUS } from '../../common/constants';

export interface RatePriceItem {
  duration: number;
  dailyPrice: number;
  dailyPurchasePrice: number;
}

export interface DefaultRateDetails {
  id?: string;
  isDeleted?: boolean;
  accommodationId: string;
  priceForAdditionalDay: number;
  dailyPurchasePrice: number;
  commissionPercent: number;
  checkInTime: string;
  checkOutTime: string;
  ratePrices: RatePriceItem[];
}

export interface DefaultRatesState {
  defaultRateDetails: DefaultRateDetails;
  defaultRateDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: DefaultRatesState = {
  defaultRateDetails: {
    id: '',
    isDeleted: false,
    accommodationId: '',
    priceForAdditionalDay: 0,
    dailyPurchasePrice: 0,
    commissionPercent: 0,
    checkInTime: '',
    checkOutTime: '',
    ratePrices: [],
  },
  defaultRateDetailsStatus: LOADING_STATUS.IDLE,
};

export const getDefaultRateDetailsAsync = createAsyncThunk(
  'defaultRates/getDefaultRateDetails',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchDefaultRateDetails(accommodationId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveDefaultRateAsync = createAsyncThunk(
  'defaultRates/addDefaultRate',
  async (defaultRateData: DefaultRateDetails, { rejectWithValue }) => {
    try {
      const response = await addDefaultRate(defaultRateData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRateAsync = createAsyncThunk(
  'defaultRates/updateDefaultRate',
  async (defaultRateData: DefaultRateDetails, { rejectWithValue }) => {
    try {
      const response = await updateDefaultRate(defaultRateData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const defaultRatesSlice = createSlice({
  name: 'defaultRates',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDefaultRateDetailsAsync.pending, (state, action) => {
        state.defaultRateDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getDefaultRateDetailsAsync.fulfilled, (state, action) => {
        state.defaultRateDetailsStatus = LOADING_STATUS.IDLE;
        state.defaultRateDetails = action.payload;
      })
      .addCase(getDefaultRateDetailsAsync.rejected, state => {
        state.defaultRateDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectDefaultRateDetails = (state: RootState) =>
  state.defaultRates.defaultRateDetails;

export const selectDefaultRateDetailsLoadingStatus = (state: RootState) =>
  state.defaultRates.defaultRateDetailsStatus;

export default defaultRatesSlice.reducer;
